import React, { useState, useEffect, useRef } from 'react';
import ReactDOM from 'react-dom';
import { useNavigate } from 'react-router-dom';
import styles from './Header.module.css';
import ArrowBackOutlinedIcon from '@mui/icons-material/ArrowBackOutlined';
import MenuIcon from '@mui/icons-material/Menu';
import CloseIcon from '@mui/icons-material/Close';
import { trackCtaClick } from '../../utils/analytics';

/**
 * Componente de Header reutilizável para toda a aplicação
 * @param {Object} props - Propriedades do componente
 * @param {string} props.logoSrc - Caminho para a imagem do logo
 * @param {boolean} props.showBackButton - Se true, exibe o botão de voltar
 * @param {string} props.backButtonPath - Caminho para onde o botão de voltar deve navegar (padrão: "/")
 * @param {boolean} props.showBetaButton - Se true, exibe o botão de convite para o beta
 * @param {boolean} props.showLoginButton - Se true, exibe o botão de login
 * @param {boolean} props.centerLogo - Se true, centraliza o logo (usado em páginas específicas como beta-access)
 * @param {boolean} props.showNavigation - Se true, exibe o menu de navegação (padrão: false)
 * @param {Function} props.onCreateAccount - Se informado, exibe "Criar conta grátis" (desktop, barra do
 *   celular e menu mobile), que chama esta função (a landing abre o modal de cadastro)
 */
function Header({
  logoSrc,
  showBackButton = false,
  backButtonPath = "/",
  showBetaButton = false,
  showLoginButton = false,
  centerLogo = false,
  showNavigation = false,
  onCreateAccount = null
}) {
  const navigate = useNavigate();
  const [scrolled, setScrolled] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [activeSection, setActiveSection] = useState(null);
  const observerRef = useRef(null);

  // Efeito para detectar o scroll da página e aplicar estilos diferentes
  useEffect(() => {
    const handleScroll = () => {
      const offset = window.scrollY;
      if (offset > 50) {
        setScrolled(true);
      } else {
        setScrolled(false);
      }
    };

    window.addEventListener('scroll', handleScroll);
    
    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, []);

  // IntersectionObserver para detectar seção ativa
  useEffect(() => {
    if (!showNavigation) return;

    const sectionIds = ['beneficios', 'funcionalidades', 'seguranca', 'planos', 'depoimentos', 'duvidas'];

    const observerOptions = {
      root: null,
      rootMargin: '-80px 0px -40% 0px',
      threshold: 0
    };

    const handleIntersect = (entries) => {
      // Encontrar a entrada mais visível que está intersectando
      const intersecting = entries.filter(e => e.isIntersecting);
      if (intersecting.length > 0) {
        // Pegar a seção com maior área visível
        const mostVisible = intersecting.reduce((prev, curr) =>
          curr.intersectionRatio > prev.intersectionRatio ? curr : prev
        );
        setActiveSection(mostVisible.target.id);
      }
    };

    observerRef.current = new IntersectionObserver(handleIntersect, observerOptions);

    sectionIds.forEach(id => {
      const el = document.getElementById(id);
      if (el) observerRef.current.observe(el);
    });

    return () => {
      if (observerRef.current) {
        observerRef.current.disconnect();
      }
    };
  }, [showNavigation]);

  // Função para scroll suave para seções usando anchor IDs
  const scrollToSection = (anchorId) => {
    const element = document.getElementById(anchorId);
    
    if (element) {
      const headerHeight = 76; // Mantendo o offset de 76px do header
      const elementPosition = element.getBoundingClientRect().top + window.scrollY - headerHeight;
      
      window.scrollTo({
        top: elementPosition,
        behavior: 'smooth'
      });
    }
    
    // Fechar menu mobile após clique e notificar MobileStickyCta
    setMobileMenuOpen(false);
    window.dispatchEvent(new CustomEvent('mobileMenuClose'));
  };

  // Itens do menu de navegação principal da landing page
  const navigationItems = [
    { label: 'Benefícios', anchorId: 'beneficios' },
    { label: 'Funcionalidades', anchorId: 'funcionalidades' },
    { label: 'Segurança', anchorId: 'seguranca' },
    { label: 'Planos', anchorId: 'planos' },
    { label: 'Dúvidas', anchorId: 'duvidas' },
  ];

  // Itens para mobile (mesmos itens)
  const mobileNavigationItems = navigationItems;

  // Criar conta: mesmo modal de cadastro dos outros CTAs da página
  const handleCreateAccount = (placement) => {
    trackCtaClick({ placement });
    if (mobileMenuOpen) {
      setMobileMenuOpen(false);
      window.dispatchEvent(new CustomEvent('mobileMenuClose'));
    }
    onCreateAccount();
  };

  // Função para alternar menu mobile — notifica MobileStickyCta via evento customizado
  const toggleMobileMenu = () => {
    const next = !mobileMenuOpen;
    setMobileMenuOpen(next);
    window.dispatchEvent(new CustomEvent(next ? 'mobileMenuOpen' : 'mobileMenuClose'));
  };

  return (
    <header className={`${styles.header} ${scrolled ? styles.headerScrolled : ''}`}>
      <div className={`${styles.headerContent} ${centerLogo ? styles.headerContentCentered : ''}`}>
        {showBackButton && (
          <button
            onClick={() => navigate(backButtonPath)}
            className={styles.backButton}
          >
            <ArrowBackOutlinedIcon />
            <span className={styles.backButtonText}>Voltar para a página inicial</span>
          </button>
        )}
        
        <div
          className={styles.logoContainer}
          onClick={() => {
            window.scrollTo({
              top: 0,
              behavior: 'smooth'
            });
          }}
          style={{ cursor: 'pointer' }}
        >
          <img src={logoSrc} alt="Gabriela.app.br" className={styles.logo} />
        </div>

        {/* Menu de navegação - apenas visível em desktop quando showNavigation=true */}
        {showNavigation && (
          <nav className={styles.navigationMenu}>
            {navigationItems.map((item, index) => (
              <button
                key={index}
                onClick={() => {
                  scrollToSection(item.anchorId);
                }}
                className={`${styles.navItem} ${activeSection === item.anchorId ? styles.navItemActive : ''}`}
              >
                {item.label}
              </button>
            ))}
          </nav>
        )}

        {/* Botão do menu hambúrguer - apenas visível em mobile quando showNavigation=true */}
        {showNavigation && (
          <button
            className={styles.mobileMenuButton}
            onClick={toggleMobileMenu}
          >
            {mobileMenuOpen ? <CloseIcon /> : <MenuIcon />}
          </button>
        )}
        
        {/* Itens secundários - posicionados à direita */}
        <nav className={styles.nav}>
          {showBetaButton && (
            <button
                onClick={() => navigate("/company-join")}
                className={styles.betaInviteButton}
              >
                Entrar na equipe da clínica
              </button>
          )}
          
          {showLoginButton && (
            <button
              onClick={() => navigate("/login")}
              className={`${styles.loginButton} ${onCreateAccount ? styles.loginButtonQuiet : ''}`}
            >
              Já sou usuário
            </button>
          )}

          {onCreateAccount && (
            <button
              onClick={() => handleCreateAccount('header_create_account')}
              className={styles.createAccountButton}
            >
              Criar conta grátis
            </button>
          )}
        </nav>
      </div>

      {/* Menu mobile overlay — renderizado via portal no document.body para escapar
          do stacking context criado pelo backdrop-filter do <header>.
          Sem o portal, position:fixed dentro de um elemento com backdrop-filter
          é relativo ao elemento pai, não à viewport. */}
      {showNavigation && mobileMenuOpen && ReactDOM.createPortal(
        <div className={styles.mobileMenuOverlay}>
          {/* Header do menu mobile com logo e botão fechar */}
          <div className={styles.mobileMenuHeader}>
            <img src={logoSrc} alt="Gabriela.app.br" className={styles.mobileMenuLogo} />
            <button
              className={styles.mobileMenuCloseButton}
              onClick={() => {
                setMobileMenuOpen(false);
                window.dispatchEvent(new CustomEvent('mobileMenuClose'));
              }}
              aria-label="Fechar menu"
            >
              <CloseIcon />
            </button>
          </div>

          {/* Área scrollável com os links de navegação */}
          <nav className={styles.mobileMenuNav}>
            {mobileNavigationItems.map((item, index) => (
              <button
                key={index}
                onClick={() => scrollToSection(item.anchorId)}
                className={`${styles.mobileNavItem} ${activeSection === item.anchorId ? styles.mobileNavItemActive : ''}`}
              >
                {item.label}
              </button>
            ))}
          </nav>

          {/* Área de CTAs fixada no rodapé — sempre visível, sem condicionais de prop */}
          <div className={styles.mobileMenuCtas}>
            {onCreateAccount && (
              <button
                onClick={() => handleCreateAccount('header_mobile_menu_create_account')}
                className={styles.mobileLoginButton}
              >
                Criar conta grátis
              </button>
            )}

            <button
              onClick={() => {
                navigate("/login");
                setMobileMenuOpen(false);
                window.dispatchEvent(new CustomEvent('mobileMenuClose'));
              }}
              className={onCreateAccount ? styles.mobileBetaButton : styles.mobileLoginButton}
            >
              Já sou usuário
            </button>

            <button
              onClick={() => {
                navigate("/company-join");
                setMobileMenuOpen(false);
                window.dispatchEvent(new CustomEvent('mobileMenuClose'));
              }}
              className={styles.mobileBetaButton}
            >
              Entrar na clínica
            </button>
          </div>
        </div>,
        document.body
      )}
    </header>
  );
}

export default Header;